import React, { useState } from 'react';
import '../../../assests/css/admin/CareerConsultation.css';
import Adminmasterlayout from '../../../layouts/Adminmasterlayout/Adminmasterlayout';

const CareerConsultation = () => {
  const [formData, setFormData] = useState({
    field: '',
    skills: '',
    certifications: '',
    goal: ''
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [aiResponse, setAiResponse] = useState(null);
  const [showResults, setShowResults] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const generateCareerSuggestions = async () => {
    setIsLoading(true);
    setShowResults(false);
    
    try {
      const response = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 2000,
          messages: [
            {
              role: "user",
              content: `You are a professional career advisor. Based on the following information, provide detailed career consultation:

Field of Interest: ${formData.field}
Current Skills: ${formData.skills}
Certifications: ${formData.certifications || 'None'}
Career Goal: ${formData.goal}

Provide your response ONLY as a valid JSON object with this exact structure (no markdown, no backticks, no additional text):
{
  "careerPaths": ["specific career path 1", "specific career path 2", "specific career path 3"],
  "requiredSkills": ["skill1", "skill2", "skill3", "skill4", "skill5"],
  "marketTrends": ["current trend 1 in this field", "current trend 2 in this field", "current trend 3 in this field"],
  "recommendedCourses": [
    {"name": "Course Name 1", "platform": "Platform Name", "duration": "X weeks/months"},
    {"name": "Course Name 2", "platform": "Platform Name", "duration": "X weeks/months"},
    {"name": "Course Name 3", "platform": "Platform Name", "duration": "X weeks/months"}
  ],
  "salaryRange": "Specific salary range with currency",
  "growthOutlook": "Detailed growth outlook description for this career field"
}`
            }
          ]
        })
      });

      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }

      const data = await response.json();
      console.log('API Response:', data);
      
      // Extract text content from the response
      let textContent = '';
      if (data.content && Array.isArray(data.content)) {
        textContent = data.content
          .filter(item => item.type === "text")
          .map(item => item.text)
          .join("\n");
      }
      
      console.log('Extracted Text:', textContent);
      
      // Clean the text and parse JSON
      let cleanText = textContent.trim();
      
      // Remove markdown code blocks if present
      cleanText = cleanText.replace(/```json\n?/g, '').replace(/```\n?/g, '');
      
      // Remove any leading/trailing whitespace
      cleanText = cleanText.trim();
      
      console.log('Cleaned Text:', cleanText);
      
      // Parse the JSON
      const parsedData = JSON.parse(cleanText);
      console.log('Parsed Data:', parsedData);
      
      setAiResponse(parsedData);
      setShowResults(true);
      
    } catch (error) {
      console.error("Detailed Error:", error);
      console.error("Error message:", error.message);
      console.error("Error stack:", error.stack);
      
      setAiResponse({
        error: `Failed to generate career suggestions: ${error.message}. Please try again.`
      });
      setShowResults(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = () => {
    // Validate required fields
    if (!formData.field.trim()) {
      alert('Please enter your Field of Interest');
      return;
    }
    if (!formData.skills.trim()) {
      alert('Please enter your Current Skills');
      return;
    }
    if (!formData.goal.trim()) {
      alert('Please enter your Career Goal');
      return;
    }
    
    generateCareerSuggestions();
  };

  const handleReset = () => {
    setFormData({
      field: '',
      skills: '',
      certifications: '',
      goal: ''
    });
    setAiResponse(null);
    setShowResults(false);
  };

  return (
    <Adminmasterlayout>
    <div className="cc-layout">
      <div className="cc-layout__container">
        <header className="cc-layout__header">
          <h1 className="cc-layout__title">Career Consultation</h1>
          <p className="cc-layout__subtitle">AI-Powered Career Guidance & Recommendations</p>
        </header>

        <div className="cc-grid">
          {/* Form Section */}
          <div className="cc-grid__card cc-grid__card--form">
            <div className="cc-grid__card-header">
              <h2 className="cc-grid__card-title">Consultation Form</h2>
              <span className="cc-grid__badge">AI-Based ⭐</span>
            </div>
            
            <div className="cc-grid__form">
              <div className="cc-grid__form-group">
                <label htmlFor="field" className="cc-grid__label">
                  Field of Interest *
                </label>
                <input
                  type="text"
                  id="field"
                  name="field"
                  value={formData.field}
                  onChange={handleInputChange}
                  placeholder="e.g., Web Development, Data Science, UI/UX Design"
                  className="cc-grid__input"
                />
              </div>

              <div className="cc-grid__form-group">
                <label htmlFor="skills" className="cc-grid__label">
                  Current Skills *
                </label>
                <textarea
                  id="skills"
                  name="skills"
                  value={formData.skills}
                  onChange={handleInputChange}
                  placeholder="List your current skills (comma-separated)"
                  className="cc-grid__textarea"
                  rows="3"
                />
              </div>

              <div className="cc-grid__form-group">
                <label htmlFor="certifications" className="cc-grid__label">
                  Certifications (Optional)
                </label>
                <textarea
                  id="certifications"
                  name="certifications"
                  value={formData.certifications}
                  onChange={handleInputChange}
                  placeholder="List your certifications or write 'None'"
                  className="cc-grid__textarea"
                  rows="2"
                />
              </div>

              <div className="cc-grid__form-group">
                <label htmlFor="goal" className="cc-grid__label">
                  Career Goal *
                </label>
                <textarea
                  id="goal"
                  name="goal"
                  value={formData.goal}
                  onChange={handleInputChange}
                  placeholder="Describe your career aspirations and objectives"
                  className="cc-grid__textarea"
                  rows="3"
                />
              </div>

              <div className="cc-grid__button-layout">
                <button 
                  onClick={handleSubmit}
                  className="cc-grid__button cc-grid__button--primary"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="cc-grid__spinner"></span>
                      Analyzing...
                    </>
                  ) : (
                    'Generate Consultation'
                  )}
                </button>
                <button 
                  onClick={handleReset}
                  className="cc-grid__button cc-grid__button--secondary"
                >
                  Reset Form
                </button>
              </div>
            </div>
          </div>

          {/* AI Output Section */}
          <div className="cc-grid__card cc-grid__card--results">
            <div className="cc-grid__card-header">
              <h2 className="cc-grid__card-title">AI Recommendations</h2>
            </div>

            {!showResults && !isLoading && (
              <div className="cc-grid__empty-state">
                <div className="cc-grid__empty-icon">🎯</div>
                <h3 className="cc-grid__empty-title">Ready to Guide Your Career</h3>
                <p className="cc-grid__empty-text">
                  Fill out the consultation form and our AI will provide personalized career recommendations, market insights, and learning paths tailored to your goals.
                </p>
              </div>
            )}

            {isLoading && (
              <div className="cc-grid__loading-state">
                <div className="cc-grid__loading-spinner"></div>
                <p className="cc-grid__loading-text">Analyzing your profile and generating personalized recommendations...</p>
              </div>
            )}

            {showResults && aiResponse && !aiResponse.error && (
              <div className="cc-grid__results">
                {/* Career Paths */}
                {aiResponse.careerPaths && aiResponse.careerPaths.length > 0 && (
                  <div className="cc-grid__result-section">
                    <h3 className="cc-grid__result-title">💼 Suggested Career Paths</h3>
                    <ul className="cc-grid__list">
                      {aiResponse.careerPaths.map((path, index) => (
                        <li key={index} className="cc-grid__list-item">{path}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Required Skills */}
                {aiResponse.requiredSkills && aiResponse.requiredSkills.length > 0 && (
                  <div className="cc-grid__result-section">
                    <h3 className="cc-grid__result-title">🎯 Required Skills</h3>
                    <div className="cc-grid__skills-layout">
                      {aiResponse.requiredSkills.map((skill, index) => (
                        <span key={index} className="cc-grid__skill-tag">{skill}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Market Trends */}
                {aiResponse.marketTrends && aiResponse.marketTrends.length > 0 && (
                  <div className="cc-grid__result-section">
                    <h3 className="cc-grid__result-title">📊 Market Trends</h3>
                    <ul className="cc-grid__list">
                      {aiResponse.marketTrends.map((trend, index) => (
                        <li key={index} className="cc-grid__list-item">{trend}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Recommended Courses */}
                {aiResponse.recommendedCourses && aiResponse.recommendedCourses.length > 0 && (
                  <div className="cc-grid__result-section">
                    <h3 className="cc-grid__result-title">📚 Recommended Courses</h3>
                    <div className="cc-grid__courses-layout">
                      {aiResponse.recommendedCourses.map((course, index) => (
                        <div key={index} className="cc-grid__course-card">
                          <h4 className="cc-grid__course-name">{course.name}</h4>
                          <div className="cc-grid__course-details">
                            <span className="cc-grid__course-platform">🎓 {course.platform}</span>
                            <span className="cc-grid__course-duration">⏱️ {course.duration}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Salary Range */}
                {aiResponse.salaryRange && (
                  <div className="cc-grid__result-section">
                    <h3 className="cc-grid__result-title">💰 Salary Range</h3>
                    <p className="cc-grid__info-text">{aiResponse.salaryRange}</p>
                  </div>
                )}

                {/* Growth Outlook */}
                {aiResponse.growthOutlook && (
                  <div className="cc-grid__result-section">
                    <h3 className="cc-grid__result-title">📈 Growth Outlook</h3>
                    <p className="cc-grid__info-text">{aiResponse.growthOutlook}</p>
                  </div>
                )}
              </div>
            )}

            {showResults && aiResponse?.error && (
              <div className="cc-grid__error-state">
                <div className="cc-grid__error-icon">⚠️</div>
                <p className="cc-grid__error-text">{aiResponse.error}</p>
                <button 
                  onClick={handleReset}
                  className="cc-grid__button cc-grid__button--primary"
                  style={{ marginTop: '1rem' }}
                >
                  Try Again
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </Adminmasterlayout>
  );
};

export default CareerConsultation;